import React, { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const NovelModal = ({ post, onClose }) => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(true);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleEscapeKey);
    return () => window.removeEventListener("keydown", handleEscapeKey);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => onClose(), 300); // Wait for animation to finish
  }, [onClose]);

  // Navigate to previous chapter
  const handlePreviousChapter = useCallback(() => {
    setCurrentChapterIndex((prev) => Math.max(0, prev - 1));
  }, []);

  // Navigate to next chapter
  const handleNextChapter = useCallback(() => {
    if (post?.chapters?.length) {
      setCurrentChapterIndex((prev) =>
        Math.min(post.chapters.length - 1, prev + 1)
      );
    }
  }, [post?.chapters?.length]);

  // Close when clicking outside modal
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  // No chapters → don't render modal
  if (!post || !post.chapters || post.chapters.length === 0) {
    return null;
  }

  const chapters = post.chapters;
  const currentChapter = chapters[currentChapterIndex];

  const isTextWhite = post.textColor === "white";
  const bgClass = post.backgroundStyle || "bg-white";

  const baseTextColor = isTextWhite ? "text-white" : "text-black";
  const subtitleTextColor = isTextWhite
    ? "text-white text-opacity-90"
    : "text-black text-opacity-90";

  return (
    <>
      <style>{`
        @keyframes fadeInBackdrop {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUpModal {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideDownModal {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }

        .modal-enter {
          animation: slideUpModal 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .modal-exit {
          animation: slideDownModal 0.3s cubic-bezier(0.64, 0, 0.78, 0) forwards;
        }

        .backdrop-enter {
          animation: fadeInBackdrop 0.4s ease-out forwards;
        }

        .backdrop-exit {
          animation: fadeInBackdrop 0.3s ease-out forwards reverse;
        }

        .smooth-scroll {
          scroll-behavior: smooth;
        }
      `}</style>

      {/* Backdrop */}
      <div
        className={`fixed inset-0 h-dvh bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center ${
          isOpen ? "backdrop-enter" : "backdrop-exit"
        }`}
        onClick={handleBackdropClick}
      >
        {/* Modal Container */}
        <div
          className={`${
            isOpen ? "modal-enter" : "modal-exit"
          } ${bgClass} w-full h-dvh sm:h-auto sm:max-h-[90vh] max-w-2xl rounded-3xl overflow-hidden flex flex-col max-[552px]:rounded-none relative shadow-2xl`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Fixed */}
          <div className="px-4 sm:px-6 py-3 bg-black/95 backdrop-blur-md border-b border-gray-800 sticky top-0 z-30">
            {/* ----------- TITLE ROW ----------- */}
            <h2 className={`text-lg sm:text-xl font-semibold text-white break-words leading-tight ${post.fontStyle}`}>
              {post.title}
            </h2>

            {/* ----------- CONTROLS ROW ----------- */}
            <div className="flex items-center justify-between mt-3">
              {/* LEFT: Chapter Counter + Nav Buttons */}
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-300 font-medium whitespace-nowrap">
                  {currentChapterIndex + 1}/{chapters.length}
                </span>

                {/* Previous Chapter */}
                <button
                  onClick={handlePreviousChapter}
                  disabled={currentChapterIndex === 0}
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                    currentChapterIndex === 0
                      ? "bg-gray-700/60 text-gray-500 cursor-not-allowed"
                      : "bg-gray-700 text-white hover:bg-gray-600 active:scale-95"
                  }`}
                  aria-label="Previous chapter"
                  title="Previous chapter"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>

                {/* Next Chapter */}
                <button
                  onClick={handleNextChapter}
                  disabled={currentChapterIndex === chapters.length - 1}
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                    currentChapterIndex === chapters.length - 1
                      ? "bg-gray-700/60 text-gray-500 cursor-not-allowed"
                      : "bg-gray-700 text-white hover:bg-gray-600 active:scale-95"
                  }`}
                  aria-label="Next chapter"
                  title="Next chapter"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* RIGHT: Close Button */}
              <button
                onClick={handleClose}
                className="text-gray-300 hover:text-white transition active:scale-95 ml-1"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>

          {/* Content Area - Scrollable */}
          <div className={`flex-1 overflow-y-auto smooth-scroll relative`}>
            {/* Chapter Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3
                className={`text-xl sm:text-2xl font-semibold ${baseTextColor} ${post.fontStyle}`}
              >
                Chapter {currentChapter.chapterNumber}
              </h3>
              <h4
                className={`text-lg sm:text-xl font-normal ${subtitleTextColor} ${post.fontStyle}`}
              >
                {currentChapter.title}
              </h4>
            </div>

            {/* Chapter Content */}
            <div className="relative">
              {isTextWhite && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              )}

              <div className="relative z-10 px-4 sm:px-6 py-4">
                <pre
                  className={`font-normal ${baseTextColor} ${post.fontStyle} leading-relaxed text-sm sm:text-base whitespace-pre-wrap font-inherit break-words`}
                >
                  {currentChapter.content}
                </pre>
              </div>
            </div>

            {/* Bottom Padding for better scroll UX */}
            <div className="h-8 sm:h-12" />
          </div>

          {/* Footer - Chapter Navigation Info */}

          <div className="px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0 border-t border-gray-200 bg-gray-900 text-xs sm:text-sm text-gray-300">
            <span className="w-[15%]">
              {currentChapterIndex + 1} of {chapters.length}
            </span>

            <span className="text-gray-500 flex-1 text-right">
              {currentChapter.title}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default NovelModal;
